/* Paise-native money helpers for the Donation Session Engine ONLY.
   The engine's DB columns store amount_paise as BIGINT PAISE. This module converts
   rupees↔paise at the two edges (donor rupee input → paise on the way in; paise →
   rupee string for display) and NOTHING refactors existing decimal-rupee code to use it.

   No float arithmetic on the way in: the rupee and paise parts are parsed as separate
   integers, so "501.50" can never drift to 5049 or 5051 paise. */

import { formatIndianNumber } from './format-number'

/* Max sane single donation: 7 digits of rupees (₹99,99,999). Guards against a
   fat-fingered / overflow amount reaching a BIGINT column. */
const MAX_RUPEE_DIGITS = 7

export class InvalidAmountError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'InvalidAmountError'
  }
}

/**
 * Parse a donor's rupee input → integer paise (safe integer, fits BIGINT).
 * Accepts "501", "501.50", "1,001", 501, 501.5. Rejects NaN, negative, zero,
 * >2 decimal places, and >7 rupee digits. Throws InvalidAmountError with a
 * donor-readable message on any rejection.
 */
export function rupeesToPaise(input: string | number): number {
  if (input === null || input === undefined) throw new InvalidAmountError('Enter an amount.')
  // Normalise: strip ₹, commas, spaces. A number is stringified first so 501.5 → "501.5".
  const cleaned = String(input).trim().replace(/[₹,\s]/g, '')
  if (cleaned === '') throw new InvalidAmountError('Enter an amount.')

  const m = cleaned.match(/^(\d+)(?:\.(\d{1,2}))?$/)
  if (!m) throw new InvalidAmountError('Enter a valid amount in rupees (up to two decimals).')

  const rupeePart = m[1]
  const paisePart = m[2] ?? ''
  if (rupeePart.replace(/^0+/, '').length > MAX_RUPEE_DIGITS) {
    throw new InvalidAmountError('That amount is too large.')
  }

  const rupeeInt = parseInt(rupeePart, 10)
  const paiseInt = paisePart === '' ? 0 : parseInt(paisePart.padEnd(2, '0'), 10)
  const paise = rupeeInt * 100 + paiseInt
  if (!Number.isFinite(paise) || paise <= 0) throw new InvalidAmountError('Enter an amount greater than zero.')
  return paise
}

/** Integer paise → number of rupees (for formatting / the UPI `am=` edge). */
export function paiseToRupees(paise: number | bigint | string): number {
  const p = typeof paise === 'bigint' ? Number(paise) : Number(paise)
  return (Number.isFinite(p) ? p : 0) / 100
}

/**
 * Integer paise → display string with Indian grouping.
 * Whole rupees show no decimals ("₹501"); sub-rupee amounts show two ("₹1,001.50").
 * Grouping is ICU-independent (reuses formatIndianNumber) so server and client render
 * byte-identical strings.
 */
export function formatPaise(paise: number | bigint | string): string {
  const p = Math.round(typeof paise === 'bigint' ? Number(paise) : Number(paise) || 0)
  const neg = p < 0
  const abs = Math.abs(p)
  const rupees = Math.floor(abs / 100)
  const rem = abs % 100
  const grouped = formatIndianNumber(rupees)
  const body = rem === 0 ? grouped : `${grouped}.${String(rem).padStart(2, '0')}`
  return `${neg ? '-' : ''}₹${body}`
}

/** The `am=` value for a UPI intent URL — rupees as a plain decimal string per the UPI spec. */
export function paiseToUpiAmount(paise: number | bigint | string): string {
  const p = Math.round(typeof paise === 'bigint' ? Number(paise) : Number(paise) || 0)
  const rupees = Math.floor(p / 100)
  const rem = p % 100
  return rem === 0 ? String(rupees) : `${rupees}.${String(rem).padStart(2, '0')}`
}
